import { ApolloError } from "apollo-client";
import React, { Component } from "react";
import { isMobile } from "react-device-detect";
import { translate } from "react-polyglot";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Col,
  Container,
  Input,
  Row,
  Table
} from "reactstrap";
import styled from "styled-components";
import { Subscribe } from "unstated";
import CheckBuildupChanges from "../components/buildup/CheckBuildupChanges";
import OfflineDataTable from "../components/buildup/OfflineDataTable";
import ParameterFilter from "../components/filter/FilterGroup";
import CustomButton from "../components/helpers/CustomButton";
import { MetsecCard, MetsecCardHeader } from "../components/MetsecCard";
import ViewBuildupModal from "../components/modals/ViewBuildup";
import Sidebar from "../components/sidebar";
import { BuildupsComponent, BuildupsQuery } from "../generated/graphql";
import parameterList from "../helpers/parameterList";
import ConnectionStatus from "../state/ConnectionStatus";
import FilterState from "../state/FilterState";
import ModalContainer from "../state/ModalContainer";
import OfflineData from "../state/OfflineData";
import MaterialImportModal from "../components/modals/MaterialImportModal";

interface IState {
  [name: string]: any;
}

interface IFilter {
  id: string;
  parameter: string;
  operator: string;
  values: string[];
}

interface IProps {
  t: (key: string) => string;
}

const HeightInput = styled(Input)`
  margin: 0.25rem !important;
`;

const ImageDiv = styled.div`
  width: 50rem;
`;

const FlexChildSmall = styled.div`
  flex-basis: fit-content;
  padding: 0.25rem;
`;

const Flexbox = styled.div`
  display: flex;
`;

const MaxHeightCard = styled(MetsecCard)`
  height: 80vh;
`;

const MaxHeightCardBody = styled(CardBody)`
  height: 60%;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 0 !important;
`;

const MetsecButton = styled(Button)`
  background-color: #0082b4 !important;
  color: white !important;
`;

class Buildups extends Component<IProps, IState> {
  public filters = [] as IFilter[];
  public state = {
    filters: [] as IFilter[],
    numberOfBuildups: 0,
    search: "",
    page: 0
  };

  constructor(props: any) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.getSearch = this.getSearch.bind(this);
    this.getFilters = this.getFilters.bind(this);
    this.handleFilterUpdate = this.handleFilterUpdate.bind(this);
    this.renderData = this.renderData.bind(this);
  }

  public nextPage() {
    this.setState({ page: this.state.page + 1 });
  }

  public prevPage() {
    const page = this.state.page <= 0 ? 0 : this.state.page - 1;
    this.setState({ page });
  }

  public render() {
    const { t } = this.props;

    return (
      <Subscribe to={[ModalContainer, FilterState]}>
        {(modal: any, filterState: any) => {
          const openUpdateModal = (buildup: any) =>
            modal.showModal(ViewBuildupModal, { buildup });

          const {
            state: { filters }
          } = filterState;

          const { addFilter } = filterState;
          const columnList = filters
            .filter((f: any) => f.parameter !== "templateId")
            .reduce((filtered: any, option: any) => {
              if (!filtered.includes(option.parameter)) {
                filtered.push(option.parameter);
              }
              return filtered;
            }, [] as string[]);

          return (
            <Container fluid={true}>
              <Row className="pr-1 pt-4">
                {isMobile ? null : (
                  <Col className="col-md-3 d-none d-xl-block">
                    <MetsecCard>
                      <MetsecCardHeader>
                        {t("labels.primaryFilters")}
                      </MetsecCardHeader>
                      <CardBody className="card-body">
                        <Sidebar />
                      </CardBody>
                    </MetsecCard>
                  </Col>
                )}
                <Col className={isMobile ? "" : "col-md-12 col-xl-9"}>
                  <Row>
                    <Col className="mb-10 mb-xl-0" xl="12">
                      <MetsecCard>
                        <MetsecCardHeader>
                          {t("labels.buildupResults")}
                        </MetsecCardHeader>
                        <CardBody className="card-body">
                          <Row className="pr-1">
                            <Col>
                              <Flexbox>
                                <HeightInput
                                  name="search"
                                  placeholder="Search..."
                                  onChange={this.handleChange}
                                />

                                {filters.length === 0 ? (
                                  <FlexChildSmall className="pt-2">
                                    <CustomButton
                                      className="shadow-none"
                                      color="success"
                                      isCircle={true}
                                      onClick={() =>
                                        addFilter("", parameterList[0].name, [
                                          ""
                                        ])
                                      }
                                    >
                                      <i className="fas fa-plus" />
                                    </CustomButton>
                                  </FlexChildSmall>
                                ) : null}
                              </Flexbox>
                            </Col>
                          </Row>
                          <ParameterFilter />
                        </CardBody>
                      </MetsecCard>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="mb-10 mb-xl-0" xl="12">
                      <MetsecCard className="mt-0">
                        <Subscribe to={[OfflineData, ConnectionStatus]}>
                          {(offlineData: any, connectionStatus: any) => {
                            const {
                              state: { buildups }
                            } = offlineData;
                            const {
                              state: { isOnline }
                            } = connectionStatus;
                            const { numberOfBuildups } = this.state;
                            return (
                              <MetsecCardHeader>
                                {t("labels.searchResults")}
                                <div style={{ float: "right" }}>
                                  {t("labels.numberOfResults")}
                                  {isOnline
                                    ? numberOfBuildups
                                    : buildups.length}
                                </div>
                              </MetsecCardHeader>
                            );
                          }}
                        </Subscribe>
                        <Table
                          className="table-flush"
                          responsive={true}
                          hover={true}
                        >
                          <thead className="thead-light">
                            <tr>
                              <th>{t("labels.name")}</th>
                              <th>{t("parameters.description")}</th>
                              <th>{t("parameters.liningsInnerLayers")}</th>
                              <th>{t("parameters.liningsFaceLayer")}</th>
                              <th>{t("parameters.insulationInternalManufacturer")}</th>
                              <th>{t("parameters.uValue")}</th>

                              {columnList.map((filter: any) => {
                                return (
                                  <th key={filter}>
                                    {t(`parameters.${filter}`)}
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            <Subscribe to={[ConnectionStatus, OfflineData]}>
                              {(status: any, offlineData: any) => {
                                const searchedBuildups = offlineData.searchBuildups(
                                  filters,
                                  this.getSearch()
                                );

                                return status.state.isOnline ? (
                                  <React.Fragment>
                                    <CheckBuildupChanges
                                      needToCheckForChanges={
                                        offlineData.state.needToCheckForChanges
                                      }
                                      setNeedToCheckForChanges={
                                        offlineData.setNeedToCheckForChanges
                                      }
                                    />
                                    <BuildupsComponent
                                      variables={{
                                        filters: this.getFilters(filters),
                                        page: this.state.page,
                                        search: this.getSearch()
                                      }}
                                    >
                                      {({ loading, error, data }) =>
                                        this.renderState(
                                          loading,
                                          error,
                                          data,
                                          openUpdateModal,
                                          columnList
                                        )
                                      }
                                    </BuildupsComponent>
                                  </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                      <OfflineDataTable
                                        searchedBuildups={searchedBuildups}
                                        openUpdateModal={openUpdateModal}
                                        columnList={columnList}
                                      />
                                    </React.Fragment>
                                  );
                              }}
                            </Subscribe>
                          </tbody>
                        </Table>
                        <CardFooter>
                          <CustomButton
                            onClick={() => this.prevPage()}
                            color="secondary"
                            className="shadow-none"
                          >
                            Previous Page
                          </CustomButton>
                          <CustomButton
                            onClick={() => this.nextPage()}
                            color="primary"
                            className="shadow-none"
                            style={{ color: "white" }}
                          >
                            Next page
                          </CustomButton>
                        </CardFooter>
                      </MetsecCard>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Container>
          );
        }}
      </Subscribe>
    );
  }

  public handleFilterUpdate(filters: IFilter[]) {
    this.setState({ filters });
  }

  private handleChange(e: any) {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  }

  private getSearch() {
    return this.state.search;
  }

  private getFilters(filters: any[]) {
    return filters.map(f => {
      return {
        id: f.id,
        operator: f.operator,
        parameter: f.parameter,
        values: f.values
      };
    });
  }

  private renderState(
    loading: boolean,
    error: ApolloError | undefined,
    data: BuildupsQuery | undefined,
    openUpdateModal: any,
    columnList: string[]
  ) {
    if (loading) {
      return this.renderLoading();
    }
    if (error) {
      return this.renderError(error);
    }

    return this.renderData(data, openUpdateModal, columnList);
  }

  private renderError(error: ApolloError | undefined) {
    const { t } = this.props;

    return (
      <tr>
        <td>
          <p>{t("errors.failedToFetch")}</p>
        </td>
      </tr>
    );
  }

  private renderLoading() {
    const { t } = this.props;
    return (
      <tr>
        <td>{t("labels.loading")}</td>
      </tr>
    );
  }

  private renderData(
    data: BuildupsQuery | undefined,
    openUpdateModal: any,
    columnList: string[]
  ) {
    const { t } = this.props;
    if (!data || !data.buildups) {
      return (
        <tr>
          <td>{t("errors.noBuildups")}</td>
        </tr>
      );
    }

    const { buildups } = data;
    if (buildups.length === 0) {
      return (
        <tr>
          <td>{t("errors.noSearchResults")}</td>
        </tr>
      );
    }
    const { numberOfBuildups } = this.state;
    if (numberOfBuildups !== buildups.length) {
      this.setState({ numberOfBuildups: buildups.length });
    }
    return buildups.map((buildup: any) => {
      const { id, name, description, isArchived, liningsInnerLayers, liningsFaceLayer, insulationInternalManufacturer, uValue, ...parameters } = buildup;

      return (
        <tr key={id} onClick={() => openUpdateModal(buildup)}>
          <td>{name}</td>
          <td>{description}</td>
          <td>{liningsInnerLayers}</td>
          <td>{liningsFaceLayer}</td>
          <td>{insulationInternalManufacturer}</td>
          <td>{uValue}</td>
          {columnList.map(p => {
            return <td key={p}>{parameters[p]}</td>;
          })}
        </tr>
      );
    });
  }
}

export default translate()(Buildups);
